/**
 * Cloud implementation of the shared CameraImportApi — node-scoped.
 *
 * ─── THE FILE TRAVELS AS TEXT ───
 *
 * The local app POSTs multipart at its own backend. That road does not exist here: the cloud
 * reaches a site through a WebSocket tunnel carrying JSON messages, and multipart cannot cross
 * it. So the workbook is read in the browser, base64-encoded, and posted as a field; the node
 * has a second door onto the identical importer that decodes it back into the identical buffer
 * (/api/internal/cameras/import). The analytics alert images cross the same way, for the same
 * reason.
 *
 * Base64 costs a third in size. The node caps an upload at 2MB of file, so the JSON body peaks
 * near 2.7MB — well inside the cloud's 25MB body limit and the tunnel's own.
 *
 * WITH NO SITE SELECTED every call fails with a stated reason rather than throwing: cameras
 * belong to a site, and "All Sites" has no node to import them into.
 */
import type { CameraImportApi, CameraImportOutcome } from '@trinetra/shared';
import api from '../services/api.service';
import { selectedNodeId } from './node';

/** A Blob → base64, without loading a second copy as a data: URL string first. */
async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  // Chunked: `String.fromCharCode(...bytes)` on a 2MB array blows the argument limit.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

const blankSummary = { total: 0, created: 0, skipped: 0, failed: 0, ignored_columns: [] };

function noSite(): CameraImportOutcome {
  return {
    ok: false,
    message: 'Pick a site first — cameras are imported into one site, not into the estate.',
    summary: { ...blankSummary },
    rows: [],
    details: [],
  };
}

export const cloudCameraImportApi: CameraImportApi = {
  async spec() {
    const nodeId = selectedNodeId();
    if (!nodeId) throw new Error('No site selected.');
    const resp = await api.get(`/nodes/${nodeId}/cameras/import/spec`);
    // The tunnel double-wraps: { success, data: { success, message, data } }.
    const outer = resp.data?.data;
    return outer && 'data' in outer ? outer.data : outer;
  },

  async upload(file, onProgress) {
    const nodeId = selectedNodeId();
    if (!nodeId) return noSite();

    onProgress?.(5); // encoding a few MB is not instant; the bar should not sit at zero
    const content_base64 = await toBase64(file);

    try {
      const resp = await api.post(
        `/nodes/${nodeId}/cameras/import`,
        { filename: file.name, content_base64 },
        {
          onUploadProgress: (e) => {
            if (e.total && onProgress) onProgress(Math.round((e.loaded * 90) / e.total) + 5);
          },
        },
      );
      const outer = resp.data?.data;
      const d = (outer && 'data' in outer ? outer.data : outer) || {};
      return {
        ok: true,
        message: (outer && outer.message) || resp.data?.message || 'Import finished.',
        summary: { ...blankSummary, ...d, rows: undefined },
        rows: d.rows || [],
        details: [],
      };
    } catch (err: any) {
      // A file whose rows all failed comes back 422 through the proxy, which flattens the node's
      // body into its own error message. Both shapes are unpacked here so the page renders one
      // kind of result whichever leg refused it.
      const body = err.response?.data;
      const e = body?.error || {};
      return {
        ok: false,
        message: body?.message || err.message || 'The import could not be completed.',
        summary: { ...blankSummary, ...e, rows: undefined, details: undefined },
        rows: e.rows || [],
        details: e.details || [],
      };
    }
  },
};
